/** C1 — "Record filing at court" (G7 facts): filing date, court reference, fee + deposit receipt, optional sealed copy → OneDrive / Court.
 *  Creates the "Petition Filed" diary event and completes "File petition at court". */
import { useState } from "react";
import { Landmark, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { recordPetitionFiling } from "@/lib/api";
import { Field, FileDrop, UploadProgress, errMsg, fmtDate, today, uploadAll } from "./petitionShared";

export default function PetitionFilingDialog({ caseRow, state, onClose, onSaved }) {
  const prep = state?.petition?.prepared || {};
  const [form, setForm] = useState({ filed_on: today(), court_ref: "", receipt_ref: "", fee: prep.fee ?? "", deposit: prep.deposit ?? "", notes: "" });
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const valid = !!form.filed_on;

  const submit = async () => {
    setBusy(true);
    try {
      const refs = await uploadAll(files.map((f) => ({ file: f, kind: "other" })), setProgress);
      const r = await recordPetitionFiling(caseRow.case_id, { ...form, fee: form.fee === "" ? null : Number(form.fee), deposit: form.deposit === "" ? null : Number(form.deposit), files: refs });
      (r.warnings || []).forEach((w) => toast.warning(w));
      const n = (r.created_events || []).length;
      toast.success(`Filing recorded${n ? ` · ${n} diary action${n === 1 ? "" : "s"}` : ""}${r.completed_actions ? ` · "File petition at court" completed` : ""}${r.ladder_stage ? ` · stage ${r.ladder_stage}` : ""}`);
      onSaved?.(r);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); setProgress(null); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-xl" data-testid="petition-filing-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm flex items-center gap-2"><Landmark className="w-4 h-4 text-violet-700" />Record filing at court — {caseRow.customer_name}</DialogTitle>
        </DialogHeader>
        <p className="text-[11px] text-muted-foreground -mt-1">Bank 1 dated {fmtDate(prep.dated)} · {prep.court?.name || "court not set"}. Without a receipt reference or sealed copy, gate G7 stays open until they are recorded (Head of Legal can override).</p>
        <div className="grid grid-cols-2 gap-3 text-xs">
          <Field label="Date filed"><Input type="date" value={form.filed_on} min={prep.dated} max={today()} onChange={set("filed_on")} className="h-8 text-xs" data-testid="filing-date" /></Field>
          <Field label="Court reference / case number"><Input value={form.court_ref} onChange={set("court_ref")} placeholder="e.g. BR-2026-000123" className="h-8 text-xs" data-testid="filing-court-ref" /></Field>
          <Field label="Fee + deposit receipt reference" hint="Proves the court fee and OR deposit were paid"><Input value={form.receipt_ref} onChange={set("receipt_ref")} className="h-8 text-xs" data-testid="filing-receipt-ref" /></Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Fee paid £"><Input type="number" step="0.01" min="0" value={form.fee} onChange={set("fee")} className="h-8 text-xs" data-testid="filing-fee" /></Field>
            <Field label="Deposit paid £"><Input type="number" step="0.01" min="0" value={form.deposit} onChange={set("deposit")} className="h-8 text-xs" data-testid="filing-deposit" /></Field>
          </div>
          <Field label="Sealed copy from the court (optional now, needed for G7)" className="col-span-2"><FileDrop files={files} setFiles={setFiles} busy={busy} label="Attach sealed copy" hint="PDF or scan → OneDrive / Court" testId="filing-sealed" /></Field>
          <Field label="Notes" className="col-span-2"><Textarea rows={2} value={form.notes} onChange={set("notes")} className="text-xs" data-testid="filing-notes" /></Field>
          <div className="col-span-2"><UploadProgress progress={progress} testId="filing-upload-progress" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy} data-testid="filing-cancel">Cancel</Button>
          <Button size="sm" className="bg-violet-700 hover:bg-violet-800" onClick={submit} disabled={busy || !valid} data-testid="filing-submit">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Landmark className="w-3.5 h-3.5 mr-1" />}{busy ? (progress ? "Uploading…" : "Saving…") : "Record filing"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
